/* DOTTO — dotto.js
   Shared runtime: focus management, drawer/modal primitives, cart API,
   section reveal. Loaded first as a module; components read window.Dotto.
   Zero dependencies. */

const trapStack = [];

function focusableIn(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:not([disabled]), [tabindex]:not([tabindex='-1']), input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])"
    )
  ).filter((el) => el.offsetParent !== null || el === document.activeElement);
}

function trapFocus(container, initial) {
  const handlers = {
    keydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = focusableIn(container);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    focusin(event) {
      if (!container.contains(event.target)) {
        const focusable = focusableIn(container);
        if (focusable.length) focusable[0].focus();
      }
    },
  };
  document.addEventListener('keydown', handlers.keydown);
  document.addEventListener('focusin', handlers.focusin);
  trapStack.push({ container, handlers, returnTo: document.activeElement });
  (initial || focusableIn(container)[0] || container).focus({ preventScroll: true });
}

function releaseFocus(container) {
  const index = trapStack.findIndex((t) => t.container === container);
  if (index === -1) return;
  const [trap] = trapStack.splice(index, 1);
  document.removeEventListener('keydown', trap.handlers.keydown);
  document.removeEventListener('focusin', trap.handlers.focusin);
  if (trap.returnTo && trap.returnTo.isConnected) trap.returnTo.focus({ preventScroll: true });
}

/* Cookies (the popup frequency cap and similar; sessionStorage is not
   reliable in all embedded contexts) */
function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/* Cart API — every mutation notifies subscribers via `cart:updated` */
function cartRequestConfig(body) {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  };
}

async function cartAdd(items) {
  const routes = window.Dotto.routes;
  const sectionsList = window.Dotto.cartSections.join(',');
  const response = await fetch(`${routes.cart_add_url}.js`, cartRequestConfig({ items, sections: sectionsList }));
  const data = await response.json();
  if (!response.ok) throw data;
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections, source: 'add' } }));
  return data;
}

async function cartChange(line, quantity) {
  const routes = window.Dotto.routes;
  const sectionsList = window.Dotto.cartSections.join(',');
  const response = await fetch(
    `${routes.cart_change_url}.js`,
    cartRequestConfig({ line, quantity, sections: sectionsList })
  );
  const data = await response.json();
  if (!response.ok) throw data;
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections, source: 'change' } }));
  return data;
}

async function cartGet() {
  const response = await fetch(`${window.Dotto.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/* Body scroll lock that survives iOS Safari: fix the body at the current
   offset on open, restore the exact offset on close. Counted so stacked
   overlays cannot double-lock or double-restore. */
let scrollLocks = 0;
let savedScrollY = 0;

function lockScroll() {
  scrollLocks += 1;
  if (scrollLocks > 1) return;
  savedScrollY = window.scrollY;
  document.body.classList.add('overlay-open');
  document.body.style.position = 'fixed';
  document.body.style.top = `-${savedScrollY}px`;
  document.body.style.width = '100%';
}

function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks > 0) return;
  document.body.classList.remove('overlay-open');
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

/* Base element for anything that opens over the page (drawers, modals).
   Markup contract: [data-panel] and [data-overlay] inside; [data-close]
   anywhere inside; toggles [data-drawer-toggle="<id>"] anywhere in the
   document. All wiring is DELEGATED — subclasses may replace their inner
   markup wholesale (the cart drawer does, on every mutation) and every
   control keeps working. `panel`/`overlay` are lazy getters for the same
   reason: they must never point at detached nodes. */
class DottoOverlay extends HTMLElement {
  get panel() {
    return this.querySelector('[data-panel]') || this;
  }

  get overlay() {
    return this.querySelector('[data-overlay]');
  }

  connectedCallback() {
    this.isOpen = false;
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]') || event.target.closest('[data-overlay]')) this.close();
    });
  }

  open(opener) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.opener = opener;
    this.panel.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    lockScroll();
    this.setAttribute('aria-hidden', 'false');
    trapFocus(this.panel);
    this.onOpen && this.onOpen();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    unlockScroll();
    this.setAttribute('aria-hidden', 'true');
    releaseFocus(this.panel);
    this.onClose && this.onClose();
  }
}

if (!customElements.get('dotto-overlay')) customElements.define('dotto-overlay', DottoOverlay);

/* One document-level listener opens/closes overlays from ANY toggle,
   including toggles rendered later (facet swaps, load-more cards,
   cross-sell chips). */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-drawer-toggle]');
  if (!toggle) return;
  const target = document.getElementById(toggle.dataset.drawerToggle);
  if (!target || typeof target.open !== 'function') return;
  event.preventDefault();
  if (target.isOpen) {
    target.close();
  } else {
    target.open(toggle);
  }
  toggle.setAttribute('aria-expanded', String(Boolean(target.isOpen)));
});

/* Details-based disclosure that closes on outside click (mega-menu) */
class DetailsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.summary = this.details.querySelector('summary');
    this.details.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.details.open) {
        this.details.open = false;
        this.summary.focus();
      }
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.details.open = false;
    });
    document.addEventListener('click', (event) => {
      if (this.details.open && !this.contains(event.target)) this.details.open = false;
    });
  }
}
if (!customElements.get('details-disclosure')) customElements.define('details-disclosure', DetailsDisclosure);

/* Section entry reveal — the single permitted motion */
function initReveal() {
  if (!document.body.classList.contains('reveal-enabled')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.body.classList.remove('reveal-enabled');
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
}

function formatMoney(cents) {
  const format = window.Dotto.moneyFormat || '${{amount}}';
  const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
  const withDecimals = (cents / 100).toFixed(2);
  return format
    .replace(/\{\{\s*amount\s*\}\}/, withDecimals)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amount);
}

window.Dotto = Object.assign(window.Dotto || {}, {
  trapFocus,
  releaseFocus,
  setCookie,
  getCookie,
  cartAdd,
  cartChange,
  cartGet,
  formatMoney,
  OverlayElement: DottoOverlay,
});

initReveal();
document.addEventListener('shopify:section:load', initReveal);
